import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {ActivatedRoute, NavigationEnd, Router} from '@angular/router';
import {isNullish, isNullishOrEmpty} from "@commonHelpers/math-utils";
import {Client as CatalogViewAPI} from '@interfaces/HttpClient/CatalogApiCatalogViewModels';
import {INode} from '@interfaces/iNode';
import {firstValueFrom} from 'rxjs';
import {filter} from 'rxjs/operators';
import {environment} from '../../environments/environment';
import {CommunicationMode, SoftwarepartnerApiSessionDto} from '../interfaces/HttpClient/AccountApiPublicModels';
import {ISoftwarepartnerApiInboundMessage, isSoftwarepartnerApiInboundMessage, SoftwarepartnerApiInboundMessageType} from '../models/softwarepartnerApi/ISoftwarepartnerApiInboundMessage';
import {ISoftwarepartnerApiMessage} from '../models/softwarepartnerApi/ISoftwarepartnerApiMessage';
import {ISoftwarepartnerApiOutboundMessage, SoftwarepartnerApiOutboundMessageType} from '../models/softwarepartnerApi/ISoftwarepartnerApiOutboundMessage';
import {SoftwarepartnerApiInteractionType} from '../models/softwarepartnerApi/SoftwarepartnerApiInteractionType';
import {GlobalVarService} from './global-var.service';
import {SoftwarepartnerApiSelectionService} from './softwarepartner-api-selection.service';
import {SoftwarepartnerApiSessionService} from './softwarepartner-api-session.service';
import {isInIframe} from '@app/helpers/iframe-helper';

// Minimaler Container fuer Inbound-Nachrichten ohne Origin-Information. Wird benutzt,
// wenn die Quelle in-process ist (Chrome WebView) oder im Test ein synthetisches Event
// uebergeben wird, das keinen MessageEvent-Origin kennt. Akzeptiert sowohl strukturierte
// Nachrichten als auch den Legacy-String 'ade_export'.
interface IInboundMessageEnvelope {
	data: ISoftwarepartnerApiInboundMessage | string;
}

@Injectable({
	providedIn: 'root'
})
export class SoftwarepartnerApiService {

	private apiSession: SoftwarepartnerApiSessionDto | null;
	private catalogItems: INode[];
	private readonly catalogViewApi: CatalogViewAPI;
	private lastSentUrl?: string;

	constructor(private globalVarService: GlobalVarService, private softwarepartnerApiSessionService: SoftwarepartnerApiSessionService, private softwarepartnerApiSelectionService: SoftwarepartnerApiSelectionService, http: HttpClient, private router: Router, private route: ActivatedRoute) {
		this.catalogViewApi = new CatalogViewAPI(http, environment.catalogApi);
	}

	public init() {
		this.softwarepartnerApiSessionService.getAsObservable().subscribe(async apiSession => {
			if (apiSession !== null && this.apiSession?.apiSessionId !== apiSession.apiSessionId) {
				this.apiSession = apiSession;
				this.postReadyHandshake();
			}
		})
		this.softwarepartnerApiSelectionService.getAsObservable().subscribe(async catalogItems => {
			this.catalogItems = catalogItems;
		})

		this.router.events
			.pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
			.subscribe(() => {
				const currentUrl = this.getRelativeUrlWithoutApiSession();
				if (currentUrl === this.lastSentUrl) return;
				if (this.postCurrentLocation(currentUrl)) {
					this.lastSentUrl = currentUrl;
				}
			});

		if ((window as any)?.chrome?.webview) {
			(window as any).chrome.webview.addEventListener('message', (event: IInboundMessageEnvelope) => {
				this.handleMessage({data: event.data})
			});
		}
		const apiSessionId = new URLSearchParams(window.location.search).get("apiSessionId");
		if (!isNullish(apiSessionId)) {
			this.globalVarService.setSoftwarePartnerSession(apiSessionId);
		}
	}

	public handleMessage(event: MessageEvent | IInboundMessageEnvelope) {
		if (isNullish(this.apiSession)) {
			return;
		}

		// Legacy-Pfad: literal-String-Vertrag mit existierenden Software-Partnern.
		// @deprecated zu Gunsten des strukturierten ISoftwarepartnerApiInboundMessage mit type='export'.
		if (event.data === 'ade_export') {
			this.postCatalogItemExport(SoftwarepartnerApiInteractionType.Button);
			return;
		}

		if (!isSoftwarepartnerApiInboundMessage(event.data)) return;

		// Origin-Validierung gilt nur fuer echte window:message-Events. Chrome-WebView-Events haben kein origin.
		if ('origin' in event) {
			const targetOrigin = this.apiSession?.postMessageTargetOrigin;
			if (isNullish(targetOrigin))
				return;

			// event.origin ist per Browser-Spec immer ohne Trailing-Slash;
			// targetOrigin kann durch URL.href-Normalisierung einen Slash haben.
			// Beide Seiten via URL.origin auf dieselbe kanonische Form bringen.
			if (event.origin !== new URL(targetOrigin).origin)
				return;
		}

		this.handleInboundMessage(event.data);
	}

	private handleInboundMessage(message: ISoftwarepartnerApiInboundMessage) {
		switch (message.type) {
			case SoftwarepartnerApiInboundMessageType.Export:
				this.postCatalogItemExport(SoftwarepartnerApiInteractionType.Button);
				break;
			case SoftwarepartnerApiInboundMessageType.NavigateToCatalogItem:
				void this.handleNavigateToCatalogItem(message.catalogItemGuid);
				break;
			case SoftwarepartnerApiInboundMessageType.NavigateToUrl:
				void this.handleNavigateToUrl(message.url);
				break;
		}
	}

	private async handleNavigateToUrl(url: string): Promise<void> {
		try {
			await this.router.navigateByUrl(url);
		} catch {
			await this.router.navigateByUrl('/notfound', { replaceUrl: true });
		}
	}

	private async handleNavigateToCatalogItem(catalogItemGuid: string) {
		let catalogKey: string | undefined;
		try {
			catalogKey = await firstValueFrom(this.catalogViewApi.catalogView_GetCatalogKeyForCatalogItemGuid(catalogItemGuid));
		} catch {
			catalogKey = undefined;
		}
		if (isNullish(catalogKey)) {
			await this.router.navigateByUrl('/notfound', { replaceUrl: true });
			return;
		}
		await this.router.navigate(['/catalog', catalogKey, 'catalogItem', catalogItemGuid], { queryParamsHandling: 'preserve' });
	}

	public postCatalogItemExport(interactionType: SoftwarepartnerApiInteractionType) {
		const catalogItemGuids = this.catalogItems?.map(node => node.guid) ?? [];
		const message: ISoftwarepartnerApiMessage = {
			type: interactionType,
			catalogItemGuids
		};
		switch (this.apiSession?.communicationMode) {
			case CommunicationMode.PostMessageIFrame:
				return this.handleIframeExport(message);
			case CommunicationMode.UrlNavigation:
				return this.handleUrlNavigationExport(message);
			case CommunicationMode.PostMessageWebView:
				return this.handleWebViewExport(message);
		}
	}

	private handleIframeExport(message: ISoftwarepartnerApiMessage) {
		if (!this.apiSession?.postMessageTargetOrigin) {
			return;
		}
		if (window.top) {
			window.top.postMessage(message, this.apiSession?.postMessageTargetOrigin)
		}
	}

	// Signalisiert dem Host nach dem Bootstrap einmalig, dass der Inbound-Listener
	// jetzt aktiv ist. Damit kann der Host eine Navigate-Nachricht erst dann posten,
	// wenn ADE sie auch empfangen kann — kein heuristisches Bootstrap-Delay noetig.
	private postReadyHandshake() {
		if (this.apiSession?.communicationMode !== CommunicationMode.PostMessageIFrame) return;
		if (!this.apiSession.postMessageTargetOrigin) return;
		if (!window.top) return;
		const message: ISoftwarepartnerApiOutboundMessage = {type: SoftwarepartnerApiOutboundMessageType.Ready};
		window.top.postMessage(message, this.apiSession.postMessageTargetOrigin);
	}

	private postCurrentLocation(url: string): boolean {
		if (this.apiSession?.communicationMode !== CommunicationMode.PostMessageIFrame) return false;
		if (!this.apiSession.postMessageTargetOrigin) return false;
		if (!isInIframe()) return false;   // statt !window.top — echter IFrame-Check
		const message: ISoftwarepartnerApiOutboundMessage = {
			type: SoftwarepartnerApiOutboundMessageType.CurrentLocation,
			url,
		};
		window.top!.postMessage(message, this.apiSession.postMessageTargetOrigin);
		return true;
	}

	private getRelativeUrlWithoutApiSession(): string {
		const fullUrl = this.router.url;
		const [pathWithQuery, fragment] = fullUrl.split('#');
		const [path, queryString] = pathWithQuery.split('?');
		if (isNullishOrEmpty(queryString)) {
			return isNullish(fragment) ? path : `${path}#${fragment}`;
		}
		const params = new URLSearchParams(queryString);
		params.delete('apiSessionId');
		const filteredQuery = params.toString();
		const result = filteredQuery.length > 0 ? `${path}?${filteredQuery}` : path;
		return isNullish(fragment) ? result : `${result}#${fragment}`;
	}

	private handleUrlNavigationExport(message: ISoftwarepartnerApiMessage) {
		void this.router.navigate(
			[],
			{
				relativeTo: this.route,
				queryParams: {catalogItemGuids: message.catalogItemGuids.join(","), interactionType: message.type},
				queryParamsHandling: 'merge'
			}).then(async () => {
			await this.router.navigate([], {
				queryParams: {
					'catalogItemGuids': null,
					'interactionType': null
				},
				queryParamsHandling: 'merge'
			});
		});
	}

	private handleWebViewExport(message: ISoftwarepartnerApiMessage) {
		if (!isNullish((window as any).chrome?.webview)) {
			(window as any).chrome.webview.postMessage(message)
		}
	}

	public hasSession() {
		return !isNullish(this.globalVarService.getSoftwarePartnerSession())
	}

	public async setSessionAsync(apiSessionId: string) {
		this.globalVarService.setSoftwarePartnerSession(apiSessionId);
		await this.softwarepartnerApiSessionService.setAsync(apiSessionId)
	}

	public getApiSessionId() {
		return this.apiSession?.apiSessionId;
	}

	public getApplicationKey() {
		return this.apiSession?.softwareId;
	}

	public setSelectedCatalogItems(markedNodes: INode[], currentNode: INode) {
		this.softwarepartnerApiSelectionService.setSelectedCatalogItems(markedNodes.length > 1 ? markedNodes : [currentNode]);
	}

	public setSelectedCatalogItemsIfSelectionEmpty(markedNodes: INode[], currentNode: INode) {
		if (isNullish(this.catalogItems) || this.catalogItems.length === 0) {
			this.softwarepartnerApiSelectionService.setSelectedCatalogItems(markedNodes.length > 1 ? markedNodes : [currentNode]);
		}
	}
}

